import { Grid, Card, CardContent, Typography, Box, LinearProgress } from '@mui/material';
import {
  FolderOpen as ProjectsIcon,
  PlayArrow as StartedIcon,
  Timeline as ProgressIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelledIcon,
} from '@mui/icons-material';
import { LoadingSkeleton } from '../../../../common/components';

/**
 * Componente de tarjetas estadísticas del dashboard de proyectos
 * Proporciona una vista rápida del estado general de los proyectos
 */
const ProjectStatsCards = ({ stats, isLoading }) => {
  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i}>
            <Card>
              <CardContent>
                <LoadingSkeleton />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  const statsData = [
    {
      title: 'Total Proyectos',
      value: stats.total,
      icon: ProjectsIcon,
      color: 'primary',
      bgColor: 'primary.50',
    },
    {
      title: 'Sin Empezar',
      value: stats.notStarted,
      icon: StartedIcon,
      color: 'grey',
      bgColor: 'grey.50',
    },
    {
      title: 'En Progreso',
      value: stats.inProgress,
      icon: ProgressIcon,
      color: 'info',
      bgColor: 'info.50',
    },
    {
      title: 'Completados',
      value: stats.completed,
      icon: CompletedIcon,
      color: 'success',
      bgColor: 'success.50',
    },
    {
      title: 'Cancelados',
      value: stats.cancelled,
      icon: CancelledIcon,
      color: 'error',
      bgColor: 'error.50',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'white', borderRadius: 1 }}>
      <Grid container spacing={2}>
        {statsData.map((stat) => {
          const Icon = stat.icon;
          return (
            <Grid item xs={6} sm={4} md={2.4} key={stat.title}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    height="100%"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={1}
                    >
                      <Typography color="text.secondary" variant="caption" fontWeight="medium">
                        {stat.title}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: stat.bgColor,
                          borderRadius: 1,
                          p: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Icon color={stat.color} sx={{ fontSize: '1.2rem' }} />
                      </Box>
                    </Box>
                    <Typography variant="h5" component="div" fontWeight="bold">
                      {stat.value}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
                >
                  Progreso general:
                </Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
                >
                  {stats.completionRate}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={parseFloat(stats.completionRate)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'success.main',
                  },
                }}
              />
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {stats.completed}/{stats.total} completados
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.inProgress} en curso
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectStatsCards;
